import React from 'react';
import { GenericTextField } from '../../GenericFieldComponents/Text';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { File } from '../../GenericFieldComponents/File';
import { FileList } from '../../GenericFieldComponents/FileList';
import { DependentTextField } from '../../GenericFieldComponents/DependentText';
import { useWorkflow } from 'src/hooks/useWorkflow';
import { GenericTagInputField } from '../../GenericFieldComponents/TagInput';
import { Funding } from '../../GenericFieldComponents/Funding';
import { FundingGeneric } from '../../GenericFieldComponents/FundingGeneric';
import { FiguresAndTables } from '../../GenericFieldComponents/FiguresAndTables';
import { PublishingModelField } from '../../GenericFieldComponents/PublishingModel';
import { GenericBooleanField } from '../../GenericFieldComponents/Boolean';
import { GenericSelectField } from '../../GenericFieldComponents/Select';
import { GenericNumberField } from '../../GenericFieldComponents/Number';
import { GenericDateField } from '../../GenericFieldComponents/Date';
import { GenericRichTextField } from '../../GenericFieldComponents/RichText';
import { AuthorsField } from '../../GenericFieldComponents/Authors';
import { ReviewersField } from '../../GenericFieldComponents/Reviewers';
import { SourceFileList } from '../../GenericFieldComponents/FileList/SourceFileList';
import { DataAvailabilityField } from '../../GenericFieldComponents/DataAvailabiityField';
import { FormFieldDataAvailability } from '@publishing-engine/domain/types/article';
import { ArticleTypeField } from '../../GenericFieldComponents/ArticleType';
import { GenericRadioField } from '../../GenericFieldComponents/Radio';
import { GenericCheckBoxArrayField } from '../../GenericFieldComponents/CheckBoxArray';
import { Figures } from '../../GenericFieldComponents/Figures';
import { GraphicalAbstractImage } from '../../GenericFieldComponents/GraphicalAbstractImage';
import { Tables } from '../../GenericFieldComponents/Tables';
import { StandardReportingGuidelinesField } from '../../GenericFieldComponents/StandardReportingGuidelines';
import { AITools } from '../../GenericFieldComponents/AITools';
import { DataAvailabilityStatementField } from '../../GenericFieldComponents/DataAvailabilityStatement';
import { IAuthorRolesMap } from 'src/utils/groupAuthorByRoles';
import { OutlineCard } from '@morressier/alembic';
import { CorrespondingAuthorFiled } from '../../GenericFieldComponents/CorrespondingAuthor';
import { SubmittingAuthorFiled } from '../../GenericFieldComponents/SubmittingAuthor';
import { isDependencySatisfied } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/asDependentFieldComponent';
import { GenericIntegerField } from '../../GenericFieldComponents/Integer';
import { GenericFocusIssueSelectField } from '../../GenericFieldComponents/FocusIssueSelect';
import { IRoleType } from '@publishing-engine/domain/models';
import { GenericCheckBoxField } from '../../GenericFieldComponents/Checkbox';
import useFocusIssueConfig from 'src/hooks/useFocusIssue';

interface Props {
  fieldName: string;
  entryPoint?: 'submission' | 'review' | 'early-assessment' | 'adjudication';
  showSubtitle?: boolean;
  showExternalDependencyEmptyState?: boolean;
  uploadCtaCopy?: string;
  // @NOTE: Custom copies is added for Radio field only, please update if you want to use for other types fields.
  customCopies?: {
    fieldLabel?: string;
    diffModalTitle?: string;
  };
  isUpdateRequired?: boolean;
  isRevisionReview?: boolean;
  isRevision?: boolean;
  roleContext?: IRoleType;
}

interface ComponentMap {
  [key: string]: {
    component: React.FC<any>;
    props?: object;
  };
}

const componentsMap: ComponentMap = {
  'System:ArticleType': {
    component: ArticleTypeField
  },
  'System:Title': {
    component: GenericRichTextField
  },
  'System:Abstract': {
    component: GenericRichTextField
  },
  'System:EnglishAbstract': {
    component: GenericRichTextField
  },
  'System:Keywords': {
    component: GenericTagInputField
  },
  'System:FiguresAndTables': {
    component: FiguresAndTables
  },
  'System:Supplementary': {
    component: FileList
  },
  'System:AdditionalSupplementary': {
    component: FileList
  },
  'System:SourceFiles': {
    component: SourceFileList
  },
  'System:Manuscript': {
    component: File,
    props: {
      referenceName: 'Manuscript'
    }
  },
  'System:TitlePage': {
    component: File,
    props: {
      referenceName: 'TitlePage'
    }
  },
  'System:AnonymizedManuscript': {
    component: File,
    props: {
      referenceName: 'Anonymized Manuscript'
    }
  },
  'System:CompleteDocumentForReview': {
    component: File,
    props: {
      referenceName: 'Complete Document for Review'
    }
  },
  'System:ConsolidatedPdf': {
    component: File,
    props: {
      referenceName: 'Consolidated PDF'
    }
  },
  'System:AnonymizedConsolidatedPdf': {
    component: File,
    props: {
      referenceName: 'Anonymized Consolidated PDF'
    }
  },
  // 'System:AnonymizedManuscriptWithTrackedChanges': {
  //   component: File,
  //   props: {
  //     referenceName: 'Anonymized with Tracked Changes'
  //   }
  // },
  'System:CoverLetter': {
    component: File,
    props: {
      referenceName: 'Cover Letter'
    }
  },
  'System:NoveltyStatement': {
    component: GenericTextField
  },
  'System:EthicsStatement': {
    component: DependentTextField
  },
  'System:ClinicalTrials': {
    component: DependentTextField
  },
  'System:AcceleratedPublication': {
    component: GenericTextField
  },
  'System:DataAvailability': {
    component: DataAvailabilityField
  },
  'System:IPEMMembership': {
    component: GenericTextField
  },
  'System:Funding': {
    component: Funding
  },
  'System:FundingGeneric': {
    component: FundingGeneric
  },
  'System:PublishingModel': {
    component: PublishingModelField
  },
  'System:TransparentPeerReview': {
    component: GenericBooleanField
  },
  'System:PreferenceImmediatePublication': {
    component: GenericBooleanField
  },
  'System:DoubleAnonymousPeerReview': {
    component: GenericBooleanField
  },
  'System:Authors': {
    component: AuthorsField
  },
  'System:RecommendedReviewers': {
    component: ReviewersField
  },
  'System:OpposedReviewers': {
    component: ReviewersField
  },
  'Custom:TextInput': {
    component: GenericTextField
  },
  'Custom:RichTextInput': {
    component: GenericRichTextField
  },
  'Custom:TextArea': {
    component: GenericTextField
  },
  'Custom:NumberInput': {
    component: GenericNumberField
  },
  'Custom:Select': {
    component: GenericSelectField
  },
  'Custom:DateInput': {
    component: GenericDateField
  },
  'Custom:RadioGroup': {
    component: GenericRadioField
  },
  'Custom:FileUploadArray': {
    component: FileList
  },
  'System:PreviouslySubmitted': {
    component: DependentTextField
  },
  'System:ConflictOfInterestForAuthor': {
    component: DependentTextField
  },
  'System:Preprint': {
    component: DependentTextField
  },
  'Custom:CheckboxArray': {
    component: GenericCheckBoxArrayField
  },
  'System:StandardReportingGuidelines': {
    component: StandardReportingGuidelinesField
  },
  'System:Figures': {
    component: Figures
  },
  'System:GraphicalAbstractImage': {
    component: GraphicalAbstractImage
  },
  'System:Tables': {
    component: Tables
  },
  'System:AITools': {
    component: AITools
  },
  'System:SubmittingAuthor': {
    component: SubmittingAuthorFiled
  },
  'System:CorrespondingAuthor': {
    component: CorrespondingAuthorFiled
  },
  'System:EnglishKeywords': {
    component: GenericTagInputField
  },
  'System:ArticleSubtype': {
    component: GenericTextField
  },
  'System:ArticleLanguage': {
    component: GenericSelectField
  },
  'System:Subtitle': {
    component: GenericRichTextField
  },
  'Custom:StringInputArray': {
    component: GenericTagInputField
  },
  'Custom:IntegerInput': {
    component: GenericIntegerField
  },
  'System:SharedDataset': {
    component: DependentTextField
  },
  'System:GeneratedDataset': {
    component: GenericBooleanField
  },
  'System:UtilizedDataset': {
    component: GenericBooleanField
  },
  'Custom:FocusSelect': {
    component: GenericFocusIssueSelectField
  },
  'System:EthicalApproval': {
    component: DependentTextField
  },
  'System:EthicsApproval': {
    component: DependentTextField
  },
  'System:CommentForEditor': {
    component: GenericRichTextField
  },
  'Custom:Checkbox': {
    component: GenericCheckBoxField
  }
};

/**
 * A Focus Issue field takes its options from the focus issue config rather than
 * from its field definition, so whether it has anything to offer can only be
 * decided where that config is available. The hook lives here, and not in
 * `GenericSubmissionField`, so only this one field subscribes to the query
 * instead of every field rendered on the page.
 */
const FocusIssueOptionsGate: React.FC<{
  submissionContextId: string;
  value: string;
  hasStaticOptions: boolean;
  children: React.ReactNode;
}> = ({ submissionContextId, value, hasStaticOptions, children }) => {
  const { validationOptions } = useFocusIssueConfig(submissionContextId, value);

  // Nothing can be shown or picked from either source: the field definition
  // carries no selectable option and the journal has no open focus issue to
  // offer. Both are needed — the definition's `options` is empty by default
  // even where the config does have focus issues, so checking it alone would
  // hide a working field.
  //
  // Only an open focus issue counts: `validationOptions` carries the closed
  // ones' ids out but no longer offers them, so the list is read for a real
  // option rather than for length. A field that already holds a value stays
  // visible whatever the journal offers now, or closing a focus issue would
  // take a made choice off the submission.
  if (
    !hasStaticOptions &&
    !value &&
    !validationOptions.some(option => option !== '')
  ) {
    return null;
  }

  return <>{children}</>;
};

export const GenericSubmissionField: React.FC<Props> = ({
  fieldName,
  entryPoint = 'submission',
  showSubtitle = true,
  showExternalDependencyEmptyState = false,
  uploadCtaCopy,
  customCopies,
  isUpdateRequired = true,
  isRevisionReview = false,
  isRevision = false,
  roleContext
}) => {
  let authorRoleMap: IAuthorRolesMap | undefined = undefined;
  const { submissionContextId, submissionId, availableActions } =
    useWorkflow(entryPoint);
  const { getFieldState, getOnChangeHandler, checkFormFieldValueIsUpdated } =
    useSubmissionFields(entryPoint);

  const ffd = getFieldState(fieldName);
  if (!ffd) return null;

  
  // @NOTE : This block added to override the DataAvailability depententOptions to DataUnavailabilityReason Options.
  if (fieldName === 'DataUnavailabilityReason' && ffd) {
    const dataAvailability = getFieldState('DataAvailability')
      ?.formFieldDefinition as FormFieldDataAvailability;
    const dependentOptions = dataAvailability.params.labels.dependentOptions;
    const orderedKeys = Object.keys(dependentOptions);

    if (
      'options' in ffd.formFieldDefinition.params.validations &&
      'options' in ffd.formFieldDefinition.params.labels
    ) {
      ffd.formFieldDefinition.params.validations.options = orderedKeys;

      const orderedOptions: Record<
        string,
        { main: string; secondary: string }
      > = {};

      for (const key of orderedKeys) {
        const value = dependentOptions[key];

        orderedOptions[key] = {
          main: key,
          secondary: value?.main || ''
        };
      }

      ffd.formFieldDefinition.params.labels.options = orderedOptions;
    }
  }

  if (fieldName === 'Authors' && ffd) {
    const contactAuthor = getFieldState('DataAvailability')?.value as
      | string
      | undefined;
    const correspondingAuthor = getFieldState('CorrespondingAuthor')?.value as
      | string
      | undefined;
    const submittingAuthor = getFieldState('SubmittingAuthor')?.value as string;

    authorRoleMap = {
      Contact: contactAuthor,
      Corresponding: correspondingAuthor,
      Submitting: submittingAuthor
    };
  }

  // @NOTE: introduced showExternalDependencyEmptyState, because we wanted to show empty state for Licenses&Permissions.
  // We can remove this if block but we need to test all the fields which has externalFieldDependency.
  const expectedValue =
    'externalFieldDependency' in ffd.formFieldDefinition.params
      ? ffd.formFieldDefinition.params.externalFieldDependency?.value
      : undefined;

  const currentValue = ffd.externalDependency?.pathValue;

  const dependencySatisfied = isDependencySatisfied(
    currentValue,
    expectedValue
  );

  if (
    'externalFieldDependency' in ffd.formFieldDefinition.params &&
    !dependencySatisfied &&
    !showExternalDependencyEmptyState
  ) {
    return null;
  }

  const onChange = getOnChangeHandler(fieldName);
  const isUpdated = checkFormFieldValueIsUpdated(fieldName);
  const Field =
    fieldName === 'DataAvailabilityStatement'
      ? {
          component: DataAvailabilityStatementField as React.FC<any>,
          props: undefined
        }
      : componentsMap[ffd.formFieldDefinition.component];
  if (!Field) return null;

  const card = (!isRevisionReview || (isRevisionReview && isUpdated)) && (
    <OutlineCard padding={4}>
      <Field.component
        {...ffd}
        {...Field.props}
        showSubtitle={showSubtitle}
        // `false` if no `onChange` handler
        isEditable={!!onChange}
        submissionContextId={submissionContextId}
        onChange={onChange}
        submissionId={submissionId}
        availableActions={availableActions}
        entryPoint={entryPoint}
        uploadCtaCopy={uploadCtaCopy}
        customCopies={customCopies}
        authorRoleMap={authorRoleMap}
        isUpdateRequired={isUpdateRequired}
        isRevision={isRevision}
        roleContext={roleContext}
      />
    </OutlineCard>
  );

  if (ffd.formFieldDefinition.component !== 'Custom:FocusSelect') {
    return card;
  }

  return (
    <FocusIssueOptionsGate
      submissionContextId={submissionContextId}
      value={String(ffd.value ?? '')}
      hasStaticOptions={Boolean(
        ffd.formFieldDefinition.params.validations.options?.some(
          option => option !== ''
        )
      )}
    >
      {card}
    </FocusIssueOptionsGate>
  );
};
